(ns practitest.frontend.tests.test-versioning
  (:require
   [ajax.core :as ajax]
   [clojure.set]
   [clojure.string :as str]
   [day8.re-frame.http-fx]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.common.mixpanel :as mixpanel]
   [practitest.frontend.components.grid.localstorage :as grid-storage]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components.modal :as modal]
   [practitest.frontend.components.notifications :as notifications]
   [practitest.frontend.components.tooltip :as tooltip]
   [practitest.frontend.components2.buttons :as buttons]
   [practitest.frontend.components2.test-type :as test-type]
   [practitest.frontend.common.dom :as dom]
   [practitest.frontend.forms3.entity-form.header :as header]
   [practitest.frontend.routing.backend-routes :as broutes]
   [practitest.frontend.subs :as subs]
   [practitest.frontend.tests.version-label-common :as label-common]
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]))

(def state-key :test-versioning)

(def versionable-fields
  {"ScriptedTest" ["name" "description" "preconditions" "steps"]
   "ExploratoryTest" ["name" "description" "preconditions" "guide_points" "charter"]
   "BDDTest" ["name" "description" "scenario"]
   "ApiTest" ["name" "description" "preconditions" "steps" "automated_test"]
   "FirecrackerTest" ["name" "description" "preconditions" "steps" "automated_test"]
   "xBotTest" ["name" "description" "automated_test"]})

(defn versionable-content-changed?
  "Checks if any versionable fields have been modified for the given test type."
  [main-form-state steps-form-state test-type]
  (let [versionable-field-names (get versionable-fields test-type)
        versionable-field-keywords (set (map keyword versionable-field-names))

        main-dirty-field-ids (->> (:fields main-form-state)
                                  (filter (fn [[_ field-data]] (:dirty? field-data)))
                                  (map first)
                                  set)

        preconditions-dirty? (get-in steps-form-state [:fields :preconditions :dirty?])
        steps-dirty? (or (seq (filter :dirty?
                                      (mapcat #(vals (:fields %))
                                              (vals (:steps steps-form-state)))))
                        (:dirty? steps-form-state))

        automation-fields-to-check (disj test-type/automated-test-fields
                                         :is_automated_clicked
                                         :automation_type)
        automated-test-dirty? (seq (clojure.set/intersection main-dirty-field-ids
                                                              automation-fields-to-check))

        all-dirty-field-ids (cond-> main-dirty-field-ids
                              preconditions-dirty? (conj :preconditions)
                              steps-dirty? (conj :steps)
                              automated-test-dirty? (conj :automated_test))]

    (boolean (seq (clojure.set/intersection versionable-field-keywords all-dirty-field-ids)))))

(re-frame/reg-sub
  ::versionable-content-changed?
  (fn [[_ test-type]]
    [(re-frame/subscribe [:practitest.frontend.forms3.entity-form.forms/form-state :test-main])
     (re-frame/subscribe [:practitest.frontend.forms3.entity-form.forms/form-state :test-steps])])
  (fn [[main-form-state steps-form-state] [_ test-type]]
    (versionable-content-changed? main-form-state steps-form-state test-type)))

(re-frame/reg-event-fx
  ::show-label-modal
  (label-common/show-modal-event state-key [::load-available-labels]))

(re-frame/reg-event-db
  ::hide-label-modal
  (fn [db _]
    (-> ((label-common/hide-modal-event state-key) db nil)
        (assoc-in [:form-states2 state-key :initial-labels] #{}))))

(re-frame/reg-event-db
  ::set-selected-labels
  (label-common/set-selection-event state-key :selected-labels))

(re-frame/reg-event-db
  ::show-label-reset-warning
  (fn [db [_ save-params]]
    (-> db
        (assoc-in [:form-states2 state-key :show-label-reset-warning?] true)
        (assoc-in [:form-states2 state-key :pending-save-params] save-params))))

(re-frame/reg-event-db
  ::hide-label-reset-warning
  (fn [db]
    (-> db
        (assoc-in [:form-states2 state-key :show-label-reset-warning?] false)
        (update-in [:form-states2 state-key] dissoc :pending-save-params))))

(re-frame/reg-event-fx
  ::confirm-save-with-label-reset
  (fn [{:keys [db]} _]
    (let [pending-save-params (get-in db [:form-states2 state-key :pending-save-params])]
      {:db (update-in db [:form-states2 state-key] dissoc :show-label-reset-warning? :pending-save-params)
       :dispatch pending-save-params})))

(re-frame/reg-event-fx
  ::load-available-labels
  (fn [{:keys [db]} [_ test-display-id]]
    {:db (update db :progress conj ::load-available-labels)
     :http-xhrio
     {:method          :get
      :uri             (broutes/get-url "version_labels#index"
                                        {:p_id (subs/get-current-project-id db)}
                                        {:test_display_id test-display-id})
      :format          (ajax/json-request-format)
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::labels-loaded]
      :on-failure      [::labels-load-error]}}))

(re-frame/reg-event-fx
  ::labels-loaded
  (fn [{:keys [db]} [_ response]]
    (let [available-labels (remove :used_in_old_versions response)
          old-labels (filter :used_in_old_versions response)
          current-labels (get-in db [:entity :test :current :version_label] [])
          current-label-ids (set (map :id current-labels))
          current-selected (set (filter #(current-label-ids (:id %)) available-labels))]
      {:db (-> db
               (assoc-in [:form-states2 state-key :available-labels] available-labels)
               (assoc-in [:form-states2 state-key :old-labels] old-labels)
               (assoc-in [:form-states2 state-key :selected-labels] current-selected)
               (assoc-in [:form-states2 state-key :initial-labels] current-selected)
               (update :progress disj ::load-available-labels))})))

(re-frame/reg-event-fx ::labels-load-error (label-common/labels-load-error-event ::load-available-labels))
(re-frame/reg-event-fx ::create-label (label-common/create-label-event [::label-created]))
(re-frame/reg-event-db ::label-created (label-common/label-created-event state-key "Label Created"))

(re-frame/reg-event-fx
  ::apply-labels
  (fn [{:keys [db]} [_ test-id selected-labels]]
    (let [label-ids (map :id selected-labels)
          labels-vec (vec selected-labels)]
      {:db (-> db
               (update :progress conj ::apply-labels)
               (assoc-in [:form-states2 state-key :pending-labels] labels-vec))
       :http-xhrio
       {:method          :post
        :uri             (broutes/get-url "version_labels#apply_to_test" {:p_id (subs/get-current-project-id db)})
        :params          {:qtest_design_id test-id
                          :label_ids label-ids}
        :format          (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success      [::labels-applied]
        :on-failure      [::labels-apply-error]}})))

(re-frame/reg-event-fx
  ::labels-applied
  (fn [{:keys [db]} _]
    (let [pending-labels (get-in db [:form-states2 state-key :pending-labels])
          project-id (subs/get-current-project-id db)
          test-id (get-in db [:entity :test :current :id])]
      (mixpanel/track "Test Versioning" {"Action" "Labels Applied"})
      {:db (-> db
               (update :progress disj ::apply-labels)
               (assoc-in [:entity :test :current :version_label] pending-labels)
               (update-in [:form-states2 state-key] dissoc :pending-labels))
       :dispatch-n [[::hide-label-modal]
                    [::notifications/success2
                     (get-translation "test.versioning.labels-applied-success" "Labels applied successfully")
                     {:auto-close true}]
                    [:practitest.frontend.events/fetch-history]
                    [::label-common/fetch-version-labels project-id test-id]]})))

(re-frame/reg-event-fx ::labels-apply-error (label-common/labels-apply-error-event ::apply-labels))
(re-frame/reg-sub ::show-modal? (label-common/show-modal-sub state-key))
(re-frame/reg-sub ::available-labels (label-common/available-labels-sub state-key))
(re-frame/reg-sub ::selected-labels (label-common/selected-labels-sub state-key))

(re-frame/reg-sub
  ::old-labels
  (fn [db]
    (get-in db [:form-states2 state-key :old-labels] [])))

(re-frame/reg-sub
  ::show-label-reset-warning?
  (fn [db]
    (get-in db [:form-states2 state-key :show-label-reset-warning?] false)))

(re-frame/reg-sub
  ::initial-labels
  (fn [db]
    (get-in db [:form-states2 state-key :initial-labels] #{})))

(re-frame/reg-sub
  ::has-call-to-test?
  (fn [db]
    (get-in db [:entity :test :current :has_call_to_test?])))

(defn version-label-modal [{:keys [test-id]}]
  (fn [{:keys [test-id]}]
    (let [show? @(re-frame/subscribe [::show-modal?])
          available-labels @(re-frame/subscribe [::available-labels])
          selected-labels @(re-frame/subscribe [::selected-labels])
          initial-labels @(re-frame/subscribe [::initial-labels])
          old-labels @(re-frame/subscribe [::old-labels])
          can-create-labels? @(re-frame/subscribe [::subs/versioning-can-create-labels?])
          has-call-to-test? @(re-frame/subscribe [::has-call-to-test?])

          ;; Create a validation function that checks if the label name matches old or current labels
          is-valid-new-label? (fn [label-name]
                                (when label-name
                                  (let [normalized-name (str/lower-case (str/trim label-name))
                                        old-label-names (set (map #(str/lower-case (:name %)) old-labels))
                                        current-label-names (set (map #(str/lower-case (:name %)) initial-labels))
                                        selected-label-names (set (map #(str/lower-case (:name %)) selected-labels))]
                                    (and (not (contains? old-label-names normalized-name))
                                         (not (contains? current-label-names normalized-name))
                                         (not (contains? selected-label-names normalized-name))))))]

      [label-common/version-label-modal
       {:title "test.versioning.modal.title"
        :title-default "Select a Version Label"
        :description "test.versioning.modal.description"
        :description-default "Add version labels for the test. Previously used labels are not displayed and cannot be reused."
        :hide-modal-event [::hide-label-modal]
        :apply-event [::apply-labels]
        :set-selection-event [::set-selected-labels]
        :create-label-event [::create-label]
        :show? show?
        :available-labels available-labels
        :selected-labels selected-labels
        :multi? true
        :placeholder (get-translation "test.versioning.unlabeled" "Unlabeled")
        :can-create-labels? can-create-labels?
        :non-removable-items initial-labels
        :initial-labels initial-labels
        :is-valid-new-label-fn is-valid-new-label?
        :warning-component (when has-call-to-test?
                            [:div.call-to-test-warning
                             [icons/mux-circle-warning]
                             [:span
                              (get-translation "test.versioning.call-to-test-warning.first"
                                               "The Test has a call to test step. The")
                              " "
                              [:strong (get-translation "test.versioning.call-to-test-warning.bold"
                                                        "called test")]
                              " "
                              (get-translation "test.versioning.call-to-test-warning.second"
                                               "will also be labeled with the same label (Better track the changes in steps)")]])
        :button-text "generic.button.save"
        :button-text-default "Save"
        :button-disabled? false
        :apply-args [test-id (vec selected-labels)]}])))

(defn label-reset-warning-modal []
  (fn []
    (let [show? @(re-frame/subscribe [::show-label-reset-warning?])]
      [modal/bootstrap-modal
       {:is-open? show?
        :on-request-close #(re-frame/dispatch [::hide-label-reset-warning])
        :close-button? true
        :centered? true
        :class "label-reset-warning-modal"}

       nil

       [:<>
        [:div.header
         (get-translation "test.versioning.label-reset-warning.title" "Label Updates")
         [:span.close-button
          {:on-click #(re-frame/dispatch [::hide-label-reset-warning])}
          [icons/mux-x]]]

        [:div.modal-content
         [:p.description.pre-line
          (get-translation "test.versioning.label-reset-warning.message"
                          "The version label will be reset after these changes are saved.\nPlease update the label if needed after the save in Test Versioning section")]
         [:div.m-checkbox-container
          [:input.m-checkbox
           {:type "checkbox"
            :id "dont-show-label-reset-warning"
            :on-change (fn [e]
                         (let [checked (-> e .-target .-checked)]
                           (grid-storage/save-hide-label-reset-warning checked)))}]
          [:label {:for "dont-show-label-reset-warning"}
           (get-translation "generic.dont-show-again" "Please do not show this message again")]]]

        [:div.modal-footer
         [buttons/button
          {:on-click #(re-frame/dispatch [::hide-label-reset-warning])
           :text (get-translation "generic.button.cancel" "Cancel")}]
         [buttons/button
          {:on-click #(re-frame/dispatch [::confirm-save-with-label-reset])
           :primary? true
           :text (get-translation "generic.button.continue" "Continue")}]]]

       nil])))

(defn test-case-versioning [form-props]
  (reagent/with-let [collapsed? (reagent/atom
                                  (grid-storage/get-section-collapsed :test "versioning" true))]
    (let [{:keys [base-entity new?]} form-props
          test-id (:id base-entity)
          test-display-id (:display_id base-entity)
          test-type (:test_type base-entity)
          current-labels (:version_label base-entity)
          snapshot? (false? (:current base-entity))
          labels-text (when (seq current-labels)
                       (str/join ", " (map :name current-labels)))
          has-content-changed? @(re-frame/subscribe [::versionable-content-changed? test-type])
          can-use-labels? @(re-frame/subscribe [::subs/versioning-can-use-labels?])
          is-bdd-test? (= test-type "BDDTest")
          button-disabled? (or snapshot? has-content-changed? (not can-use-labels?) new? is-bdd-test?)]

      [:<>
       [version-label-modal {:test-id test-id}]

       [:div.side-panel-section
        [:div.fields-panel
         {:class (dom/classes-pairs-v :section-collapsed @collapsed?)}
         [:div.header
          {:class (dom/classes-pairs-v :collapsed @collapsed?)}
          [buttons/icon-button
           {:icon (if @collapsed?
                    [icons/mux-chevron-right]
                    [icons/mux-chevron-down2])
            :on-click #(let [new-state (not @collapsed?)]
                         (reset! collapsed? new-state)
                         (grid-storage/set-section-collapsed :test "versioning" new-state))}]
          [:div.title
           (get-translation "test.versioning.title" "Test Versioning")]
          [buttons/button
           {:class "manage-versions-button"
            :icon [icons/mux-pencil]
            :tertiary? true
            :disabled? button-disabled?
            :text (get-translation "test.versioning.manage" "Manage")
            :on-click (fn []
                        (when-not button-disabled?
                          (header/set-active-tab form-props :version-labels)))}]]
         (when-not @collapsed?
           [:<>
            [:div.g2-field-container
             [:span.m-label (get-translation "test.versioning.current-label" "Current Version Label")]
             [:div.m-readonly-field.flex-full
              [:span (or labels-text (get-translation "test.versioning.unlabeled" "Unlabeled"))]]]
            [:div.version-button-container
             (if button-disabled?
               [tooltip/mux-tooltip
                {}
                (cond
                  is-bdd-test?
                  (get-translation "test.versioning.bdd-disabled" "BDD type tests can not be labeled")

                  snapshot?
                  (get-translation "test.versioning.snapshot-disabled" "Cannot set version labels on old versions")

                  (not can-use-labels?)
                  (get-translation "test.versioning.cannot-use-labels" "You don't have permission to set labels")

                  has-content-changed?
                  (get-translation "test.versioning.save-changes-disabled" "Please save your changes first")

                  new?
                  (get-translation "test.versioning.new-form-disabled" "Please save this test first to set a version label")

                  :else "")
                (fn [props]
                  [:div props
                   [buttons/button
                    {:on-click (fn [])
                     :disabled? true
                     :primary? true
                     :icon [icons/mux-pencil]
                     :text (get-translation "test.versioning.set-label" "Set Version Label")}]])]
               [buttons/button
                {:on-click #(re-frame/dispatch [::show-label-modal test-display-id])
                 :primary? true
                 :icon [icons/mux-pencil]
                 :text (get-translation "test.versioning.set-label" "Set Version Label")}])]])]]])))
